import { remToPx, stripUnit } from 'polished';
import { useEffect, useRef, useState } from 'react';
import type { FunctionComponent, CSSProperties } from 'react';

import { useArrows, useElement, useI18n, useTheme } from '../../hooks';
import type { ForwardProps } from '../../types';
import { tryCatch } from '../../utils';
import Flex from '../Flex';
import Text from '../Text';
import Tooltip from '../Tooltip';
import Button from '../Button';
import InfoDialog from '../Dialog/InfoDialog';

import {
  CurrentStepPopover,
  StepMarker,
  StyledBar,
  StyledFill,
  StyledFormProgress,
  StyledHorizontalStepText,
  StyleHorizontalStepMarkers
} from './FormProgress.styles';
import type { MultiStepFormProps } from './MultiStepForm.types';
import VerticalFormProgress from './VerticalFormProgress';
import { useFormProgressOverflow } from './useFormProgressOverflow';

interface FormProgressProps {
  steps: MultiStepFormProps['steps'];
  currentStepId: MultiStepFormProps['currentStepId'];
  showStepNames?: boolean;
}

const HorizontalFormProgress: FunctionComponent<FormProgressProps & ForwardProps> = ({
  steps,
  currentStepId,
  showStepNames = false,
  ...restProps
}) => {
  const t = useI18n();

  const [stepsDialogButtonEl, setStepsDialogButtonEl] = useElement<HTMLButtonElement>();
  const [showStepsDialog, setShowStepsDialog] = useState(false);

  const { ref: formProgressRef, state: stepsAreOverflowing } = useFormProgressOverflow(
    steps.length,
    showStepNames
  );

  const curIdx = steps.findIndex(step => step.id === currentStepId);
  const curStep = steps[curIdx];
  const fillScale = curIdx / (steps.length - 1);

  const [currentStepEl, setCurrentStepEl] = useElement();
  const [stepEls, setStepEls] = useState<Record<string, HTMLElement>>({});
  const stepsRef = useRef(null);
  const theme = useTheme();

  let popoverPlacement: 'bottom' | 'bottom-start' | 'bottom-end' = 'bottom';
  if (curIdx === 0) popoverPlacement = 'bottom-start';
  else if (curIdx === steps.length - 1) popoverPlacement = 'bottom-end';

  useArrows(stepsRef, {
    selector: '[data-step-marker]',
    dir: 'left-right',
    initialFocusElement: currentStepEl
  });

  useArrows(stepsRef, {
    selector: '[data-step-marker]',
    cycle: false,
    dir: 'up-down',
    initialFocusElement: currentStepEl
  });

  useEffect(() => {
    if (stepEls[curStep.id] && stepEls[curStep.id] !== currentStepEl) {
      setCurrentStepEl(stepEls[curStep.id]);
    }
  }, [Object.keys(stepEls), curStep]);

  useEffect(() => {
    if (!stepsAreOverflowing) {
      setShowStepsDialog(false);
    }
  }, [stepsAreOverflowing]);

  const numberOfSteps = steps.length;

  return (
    <StyledFormProgress
      ref={formProgressRef}
      {...restProps}
      container={{ alignItems: 'center' }}
      showStepNames={showStepNames}
    >
      <CurrentStepPopover
        show={!showStepNames}
        target={currentStepEl}
        placement={popoverPlacement}
        strategy='absolute'
        portal={false}
        hideOnTargetHidden
        modifiers={[
          {
            name: 'offset',
            options: {
              offset: [
                0,
                tryCatch(
                  () => stripUnit(remToPx(theme.base.spacing)),
                  () => 8
                )
              ]
            }
          },
          {
            name: 'placeAndContain',
            enabled: false
          },
          {
            name: 'flip',
            enabled: false
          },
          {
            name: 'preventOverflow',
            options: {
              padding: 0
            }
          }
        ]}
      >
        <Text variant='h4'>{curStep.name}</Text>
      </CurrentStepPopover>

      {stepsAreOverflowing ? (
        <div>
          <Button
            ref={setStepsDialogButtonEl}
            variant='link'
            onClick={() => {
              setShowStepsDialog(true);
            }}
          >
            {t('step_num', [curIdx + 1, numberOfSteps])}
          </Button>
          <Text variant='h4'>{curStep.name}</Text>
          {stepsDialogButtonEl && showStepsDialog && (
            <InfoDialog
              heading={t('steps')}
              target={stepsDialogButtonEl}
              onDismiss={() => {
                setShowStepsDialog(false);
              }}
              placement='bottom-start'
            >
              <VerticalFormProgress steps={steps} currentStepId={currentStepId} />
            </InfoDialog>
          )}
        </div>
      ) : (
        <>
          <Flex
            ref={stepsRef}
            as={StyleHorizontalStepMarkers}
            container={{ justify: 'between', alignItems: 'start' }}
            item={{ grow: 1 }}
            showStepNames={showStepNames}
          >
            {steps.map((step, index) => {
              const isCurrent = index === curIdx;
              return (
                <li key={step.id}>
                  <StepMarker
                    ref={(el: HTMLElement | null) => {
                      setStepEls(curr => {
                        const newEls = { ...curr };
                        if (!el && newEls[step.id]) {
                          delete newEls[step.id];
                          return newEls;
                        }
                        if (el) return { ...newEls, [step.id]: el };
                        return newEls;
                      });
                    }}
                    id={step.id}
                    data-step-marker=''
                    aria-label={`${step.name} - ${t('step_num', [index + 1, steps.length])}`}
                    aria-current={isCurrent ? 'step' : undefined}
                    tabIndex={isCurrent ? 0 : -1}
                    current={isCurrent}
                    depth={step.depth}
                    completed={step.completed}
                    onClick={step.onMarkerClick}
                    clickable={!!step.onMarkerClick && !isCurrent}
                  >
                    {!showStepNames && !isCurrent && (
                      <Tooltip
                        target={stepEls[step.id]}
                        showDelay='none'
                        hideDelay='short'
                        describeTarget={false}
                      >
                        {step.name}
                      </Tooltip>
                    )}
                  </StepMarker>
                  {showStepNames &&
                    (!!step.onMarkerClick && !isCurrent ? (
                      <Button
                        variant='link'
                        onClick={step.onMarkerClick}
                        as={StyledHorizontalStepText}
                        tabIndex={-1}
                      >
                        {step.name}
                      </Button>
                    ) : (
                      <Text variant={isCurrent ? 'h3' : undefined} as={StyledHorizontalStepText}>
                        {step.name}
                      </Text>
                    ))}
                </li>
              );
            })}
          </Flex>
          <StyledBar shifted={showStepNames}>
            <StyledFill style={{ '--fillScale': fillScale } as CSSProperties} />
          </StyledBar>
        </>
      )}
    </StyledFormProgress>
  );
};

export default HorizontalFormProgress;
